import AirbnbCancellationPolicyField from 'components/domain/airbnb/AirbnbCancellationPolicyField';
import BookingDotComCancellationPolicyField from 'components/domain/bookingDotCom/BookingDotComCancellationPolicyField';
import useFetchAgencyChannelSettings from 'components/domain/channelSettings/useFetchAgencyChannelSettings';
import VrboCancellationPolicyField from 'components/domain/vrbo/VrboCancellationPolicyField';
import VrboCustomTiersField from 'components/domain/vrbo/VrboCustomTiersField';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { Channel, VrboCancellationPolicy } from 'models/Channels';
import AirbnbIcon from 'assets/images/icons/channels/airbnb.svg?react';
import HvmiIcon from 'assets/images/icons/channels/hvmi.svg?react';
import BookingIcon from 'assets/images/icons/channels/booking.svg?react';
import VrboIcon from 'assets/images/icons/channels/vrbo.svg?react';
import {
  SeasonalRuleHvmbPolicyCode,
  VrboCustomTier,
} from 'models/seasonality/SeasonalRule';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getKnownAirbnbCancellationPolicyOptions } from 'utils/airbnb/cancellationPolicyUtils';
import { normalizeKey } from 'utils/localeUtils';
import useFeatures from 'hooks/useFeatures';
import ChannelPolicyField from '../fields/ChannelPolicyField';
import HostfullyPolicyField from '../fields/HostfullyPolicyField';
import {
  IconContainer,
  LabelContainer,
  TabContentStyled,
} from '../SeasonalRuleAddEditModal.styles';
import { SeasonalRuleFormValues } from '../SeasonalRuleAddEditModal.types';

const HVMB_OPTIONS: SeasonalRuleHvmbPolicyCode[] = [
  SeasonalRuleHvmbPolicyCode.CANCELLATION_14_DAYS_WITH_GRACE_PERIOD,
  SeasonalRuleHvmbPolicyCode.CANCELLATION_30_DAYS_WITH_GRACE_PERIOD,
  SeasonalRuleHvmbPolicyCode.CANCELLATION_60_DAYS_WITH_GRACE_PERIOD,
  SeasonalRuleHvmbPolicyCode.CANCELLATION_90_DAYS_WITH_GRACE_PERIOD,
];

const CancellationPolicyTab = () => {
  const { t } = useTranslation();
  const {
    allowAirbnbSeasonalCancellationPolicies,
    allowVrboSeasonalCancellationPolicies,
    allowBcSeasonalCancellationPolicies,
    allowHvmbSeasonalCancellationPolicies,
  } = useFeatures();
  const { channelSettings, isFetching: isFetchingChannelSettings } =
    useFetchAgencyChannelSettings(
      allowAirbnbSeasonalCancellationPolicies ||
        allowVrboSeasonalCancellationPolicies ||
        allowBcSeasonalCancellationPolicies ||
        allowHvmbSeasonalCancellationPolicies,
    );
  const { setValue, getValues } = useFormContext<SeasonalRuleFormValues>();
  const policies = useWatch<SeasonalRuleFormValues, 'policies'>({
    name: 'policies',
  });

  if (isFetchingChannelSettings) {
    return (
      <TabContentStyled data-testid="seasonal-rule-modal-cancellation-policy">
        <LoadingPlaceholder linesCount={5} />
      </TabContentStyled>
    );
  }

  const vrboCode = policies?.vrboCode;

  const isChannelActive = (channel: Channel) =>
    !!channelSettings?.some(
      ({ enumId, isActive }) => enumId === channel && isActive,
    );

  const showAirbnb =
    allowAirbnbSeasonalCancellationPolicies &&
    (isChannelActive(Channel.airbnb) || !!policies?.airbnbCode);
  const showVrbo =
    allowVrboSeasonalCancellationPolicies &&
    (isChannelActive(Channel.homeaway) || !!vrboCode);
  const showBcom =
    allowBcSeasonalCancellationPolicies &&
    (isChannelActive(Channel.booking_com) ||
      policies?.bookingDotComCode != null);
  const showHvmb =
    allowHvmbSeasonalCancellationPolicies &&
    (isChannelActive(Channel.hvmi) || !!policies?.hvmbCode);

  const hvmbOptions = HVMB_OPTIONS.map((code) => ({
    value: code as string,
    label: t(
      normalizeKey(
        `pageProperty.seasonality.modal.fields.hvmbPolicy.options.${code}`,
      ),
    ) as string,
  }));

  return (
    <TabContentStyled data-testid="seasonal-rule-modal-cancellation-policy">
      <HostfullyPolicyField />
      {showAirbnb && (
        <AirbnbCancellationPolicyField
          name="policies.airbnbCode"
          label={
            <LabelContainer>
              <IconContainer>
                <AirbnbIcon data-testid="airbnb-icon" />
              </IconContainer>
              {t('pageProperty.seasonality.modal.fields.airbnbPolicy.label')}
            </LabelContainer>
          }
          options={getKnownAirbnbCancellationPolicyOptions(t)}
          colSmInput={12}
          colSmLabel={12}
          labelOnTop
          isSearchable={false}
          isClearable
          showTooltip={false}
          dataTestId="seasonal-rule-airbnb-policy"
        />
      )}
      {showVrbo && (
        <>
          <VrboCancellationPolicyField
            name="policies.vrboCode"
            label={
              <LabelContainer>
                <IconContainer>
                  <VrboIcon data-testid="vrbo-icon" />
                </IconContainer>
                {t('pageProperty.seasonality.modal.fields.vrboPolicy.label')}
              </LabelContainer>
            }
            placeholder={t(
              'pageProperty.seasonality.modal.fields.vrboPolicy.placeholder',
            )}
            colSmInput={12}
            colSmLabel={12}
            labelOnTop
            isSearchable={false}
            isClearable
            showTooltip={false}
            onCustomSelected={() => {
              if (!getValues('policies.vrboCustom')?.customTiers?.length) {
                setValue('policies.vrboCustom', {
                  customTiers: [
                    {
                      tierOrder: 1,
                      refundPercentage: 0,
                      refundThreshold: 0,
                    },
                  ],
                  displayLabel: '',
                });
              }
            }}
            dataTestId="seasonal-rule-vrbo-policy"
          />
          {vrboCode === VrboCancellationPolicy.CUSTOM && (
            <VrboCustomTiersField<VrboCustomTier>
              name="policies.vrboCustom.customTiers"
              getEmptyTier={(current) => ({
                tierOrder: current.length + 1,
                refundPercentage: 0,
                refundThreshold: 0,
              })}
              getTierKey={(tier) => String(tier.tierOrder)}
            />
          )}
        </>
      )}
      {showBcom && (
        <BookingDotComCancellationPolicyField
          name="policies.bookingDotComCode"
          label={
            <LabelContainer>
              <IconContainer>
                <BookingIcon data-testid="bookingdotcom-icon" />
              </IconContainer>
              {t(
                'pageProperty.seasonality.modal.fields.bookingDotComPolicy.label',
              )}
            </LabelContainer>
          }
          placeholder={t(
            'pageProperty.seasonality.modal.fields.bookingDotComPolicy.placeholder',
          )}
          colSmInput={12}
          colSmLabel={12}
          labelOnTop
          isSearchable={false}
          isClearable
          dataTestId="seasonal-rule-bookingdotcom-policy"
        />
      )}
      {showHvmb && (
        <ChannelPolicyField
          fieldName="policies.hvmbCode"
          label={
            <LabelContainer>
              <IconContainer>
                <HvmiIcon data-testid="hvmi-icon" />
              </IconContainer>
              {t('pageProperty.seasonality.modal.fields.hvmbPolicy.label')}
            </LabelContainer>
          }
          placeholder={t(
            'pageProperty.seasonality.modal.fields.hvmbPolicy.placeholder',
          )}
          options={hvmbOptions}
          dataTestId="seasonal-rule-hvmb-policy"
        />
      )}
    </TabContentStyled>
  );
};

export default CancellationPolicyTab;
